/**
 * **The thank-you letter and the volunteer certificate** (Stream D, D05 — People.dc.html): after the
 * festival, a letter to every volunteer with their own hours and areas, and the year's together
 * line; a certificate for anyone who asks. Hours, areas and the year are the roster's (Volunteers'
 * service hours — counted lines only). A letter is signed by the named person who prints and sends
 * it, never by CIDER; the certificate's two lines are named from Roles where the reader may see them.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../shell/api.js';
import { useLoad } from '../views/admin/shared.js';
import { usePrinted } from './printed.js';
import { SheetPages } from './sheets.js';
import { usePhase } from '../shell/phase.js';

interface Worked {
  personId: string;
  name: string;
  minutes: number;
  areas: string[];
}
interface Page {
  total: number;
  offset: number;
  limit: number;
  rows: Worked[];
  together: { people: number; minutes: number };
}

/** Every page until the total is in hand — an export never stops at its own cap. */
function useEveryone(year: string): {
  rows: Worked[];
  together: Page['together'] | null;
  error: string | null;
} {
  const [state, setState] = useState<{
    rows: Worked[];
    together: Page['together'] | null;
    error: string | null;
  }>({
    rows: [],
    together: null,
    error: null,
  });
  useEffect(() => {
    const run = { live: true };
    void (async () => {
      const rows: Worked[] = [];
      let together: Page['together'] | null = null;
      try {
        for (let offset = 0; ; offset += 200) {
          const p = await api<Page>(
            'GET',
            `/volunteers/service-hours/${year}/everyone?offset=${String(offset)}&limit=200`,
          );
          rows.push(...p.rows);
          together = p.together;
          if (rows.length >= p.total || p.rows.length === 0) break;
        }
        if (run.live) setState({ rows, together, error: null });
      } catch (e) {
        if (run.live)
          setState({ rows: [], together: null, error: e instanceof Error ? e.message : String(e) });
      }
    })();
    return () => {
      run.live = false;
    };
  }, [year]);
  return state;
}

const hoursWords = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h)} ${h === 1 ? 'hour' : 'hours'}${m ? ` and ${String(m)} minutes` : ''}`;
};

export function ThankYouLetters({ year }: { year: string }): ReactNode {
  const me = useLoad<{ name?: string }>('/auth/me');
  const all = useEveryone(year);
  const ready = !!all.together && !!me.data;
  usePrinted('thank-you', ready, { year: Number(year) });
  if (all.error) return <p role="alert">{all.error}</p>;
  if (!ready) return <p>Reading the year’s hours…</p>;
  const signer = me.data?.name ?? null;
  const t = all.together;
  if (all.rows.length === 0) return <p>No counted hours for {year} yet.</p>;
  return (
    <>
      {all.rows.map((w) => (
        <SheetPages
          key={w.personId}
          doc={{
            size: 'half',
            title: 'Thank you',
            sub: `To ${w.name} · Apple Fest ${year}`,
            name: 'Thank-you letter · hours from check-outs',
            lead: (
              <>
                <p className="pr-sub">
                  {w.name.split(/\s+/)[0]} — you gave Apple Fest {hoursWords(w.minutes)} this year
                  {w.areas.length ? `, in ${w.areas.join(' and ')}` : ''}.{' '}
                  {t
                    ? `${String(t.people)} of you gave ${String(Math.round(t.minutes / 60))} hours together.`
                    : ''}{' '}
                  Thank you.
                </p>
                <p className="pr-sub">— {signer ?? '________________'}, for the directors</p>
              </>
            ),
            lines: [],
          }}
        />
      ))}
    </>
  );
}

interface RoleRow {
  name: string;
  role: string;
  state: string;
}

export function Certificate({ year, personId }: { year: string; personId: string }): ReactNode {
  const all = useEveryone(year);
  const roles = useLoad<RoleRow[]>(`/admin/roles/year?year=${year}`);
  const w = all.rows.find((r) => r.personId === personId) ?? null;
  usePrinted('certificate', !!w, { ref: personId, year: Number(year) });
  if (all.error) return <p role="alert">{all.error}</p>;
  if (!all.together) return <p>Reading the hours…</p>;
  if (!w) return <p role="alert">No counted hours for this person in {year}.</p>;
  const one = (role: string): string | null => {
    const live = (roles.data ?? []).filter(
      (r) => r.role === role && (r.state === 'granted' || r.state === 'active'),
    );
    return live.length === 1 ? (live[0]?.name ?? null) : null;
  };
  const vc = one('volunteer_chair');
  return (
    <SheetPages
      doc={{
        size: 'half',
        title: 'Certificate of volunteer service',
        sub: `Saint Lawrence Apple Fest · ${year}`,
        name: 'Volunteer certificate · hours from check-outs',
        lead: (
          <>
            <p className="pr-big">{w.name}</p>
            <p className="pr-sub">
              {hoursWords(w.minutes)}
              {w.areas.length ? ` · ${w.areas.join(' and ')}` : ''} · {year}
            </p>
          </>
        ),
        lines: [],
        sign: [vc ? `${vc} · volunteer chair` : 'Volunteer chair', 'Director'],
      }}
    />
  );
}

interface Standing {
  familyId: string;
  name: string;
  status: string;
  says: string;
  missing: string | null;
  from: { what: string; says: string }[];
}
interface Site {
  festival: { friday: string; saturday: string; sunday: string } | null;
}
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const fullDate = (ymd: string): string =>
  `${MONTHS[Number(ymd.slice(5, 7)) - 1] ?? ''} ${String(Number(ymd.slice(8, 10)))}`;
const festivalWords = (s: Site['festival']): string =>
  s
    ? `${fullDate(s.friday)}, ${String(Number(s.saturday.slice(8, 10)))} and ${String(Number(s.sunday.slice(8, 10)))}`
    : 'the weekend after Labor Day';

/**
 * **The family's-year letter** (D03): one letter to every family — where it stands, in the words
 * of Where each family stands, what it has done, and the address of its own page. Signed by the
 * person who prints and sends it.
 */
export function FamilyLetters({ year }: { year: string }): ReactNode {
  const me = useLoad<{ name?: string }>('/auth/me');
  const fam = useLoad<{ rows: Standing[] }>(`/volunteers/families?year=${year}`);
  const site = useLoad<Site>('/public/site');
  // The family page lives on the public site, wherever SITE_ORIGIN says that is.
  const { siteOrigin } = usePhase();
  const ready = !!fam.data && !!me.data && siteOrigin !== null;
  usePrinted('family-letters', ready, { year: Number(year) });
  if (fam.error) return <p role="alert">{fam.error}</p>;
  if (!ready) return <p>Reading the families…</p>;
  const rows = fam.data?.rows ?? [];
  if (rows.length === 0) return <p>No families for {year} yet.</p>;
  return (
    <>
      {rows.map((f) => (
        <SheetPages
          key={f.familyId}
          doc={{
            size: 'letter',
            title: `Your Apple Fest · ${year}`,
            sub: `To the ${f.name} family`,
            qr: `${siteOrigin}/family`,
            name: 'Family’s-year letter · one per household',
            lead: (
              <p className="pr-sub">
                Apple Fest is {festivalWords(site.data?.festival ?? null)}. Here is your year in one
                page; the QR opens your family’s page, where all of it lives.
              </p>
            ),
            cols: ['mid', 'wide'],
            lines: [
              { head: 'WHERE YOU STAND' },
              { cells: [f.status, f.says] },
              ...(f.missing ? [{ cells: ['Still to do', f.missing] }] : []),
              ...f.from.map((x) => ({ key: x.what, cells: [x.what, x.says] })),
              { head: 'AT THE FESTIVAL' },
              { cells: ['Check in at the booth with the QR. You will sign the waiver there.'] },
            ],
            sign: [me.data?.name ? `${me.data.name}, for the directors` : 'For the directors'],
          }}
        />
      ))}
    </>
  );
}

/** **The bulletin signup card** (D03): 6×4 for the parish bulletin — the dates and the family page. */
export function BulletinCard({ year }: { year: string }): ReactNode {
  const site = useLoad<Site>('/public/site');
  const { siteOrigin } = usePhase();
  usePrinted('bulletin-card', !!site.data && siteOrigin !== null, { year: Number(year) });
  if (!site.data || siteOrigin === null) return <p>Reading the festival’s dates…</p>;
  return (
    <SheetPages
      doc={{
        size: 'card-landscape',
        title: 'Apple Fest needs you',
        sub: festivalWords(site.data.festival),
        qr: `${siteOrigin}/family`,
        name: 'Bulletin card',
        lead: (
          <>
            <p className="pr-big">Help</p>
            <p className="pr-sub">Sponsor · a basket · raffle tickets · a shift</p>
          </>
        ),
        lines: [{ cells: ['Scan to open your family’s page.'] }],
      }}
    />
  );
}

interface ChairRole {
  personId: string;
  name: string;
  role: string;
  areaName?: string | null;
  named?: boolean;
  state: string;
}
interface Prospect {
  personId: string;
  name: string;
  areaNames: string[];
  state: string;
}

/**
 * **The chair invitation and "would you run an area?"** (D04): to this year's chairs, one ask for
 * next year, by area; to the people who offered to lead (the chair prospects), one ask and what a
 * chair does. Plain words, one ask. Signed by the person who prints and sends them.
 */
export function ChairLetters({
  year,
  who,
}: {
  year: string;
  who: 'returning' | 'prospects';
}): ReactNode {
  const next = String(Number(year) + 1);
  const me = useLoad<{ name?: string }>('/auth/me');
  const roles = useLoad<ChairRole[]>(who === 'returning' ? `/admin/roles/year?year=${year}` : null);
  const prospects = useLoad<Prospect[]>(
    who === 'prospects' ? `/admin/chairs/prospects?year=${next}` : null,
  );
  const settings = useLoad<{ setting: { budgetMeetingOn?: string | null } | null }>(
    `/admin/year-settings/${next}`,
  );
  const ready = !!me.data && (who === 'returning' ? !!roles.data : !!prospects.data);
  usePrinted(who === 'returning' ? 'chair-invitations' : 'prospect-letters', ready, {
    year: Number(year),
  });
  const error = roles.error ?? prospects.error;
  if (error) return <p role="alert">{error}</p>;
  if (!ready) return <p>Reading the chairs…</p>;
  const signer = me.data?.name ? `— ${me.data.name}` : '— ________________';
  const meeting = settings.data?.setting?.budgetMeetingOn ?? null;
  const people =
    who === 'returning'
      ? (roles.data ?? [])
          .filter(
            (r) =>
              r.role === 'area_chair' &&
              r.named !== false &&
              (r.state === 'granted' || r.state === 'active'),
          )
          .map((r) => ({
            key: `${r.personId}${r.areaName ?? ''}`,
            name: r.name,
            area: r.areaName ?? 'your area',
          }))
      : (prospects.data ?? [])
          .filter((p) => p.state !== 'declined')
          .map((p) => ({ key: p.personId, name: p.name, area: p.areaNames.join(' or ') }));
  if (people.length === 0)
    return (
      <p>
        {who === 'returning' ? `No chairs for ${year} yet.` : `No chair prospects for ${next} yet.`}
      </p>
    );
  return (
    <>
      {people.map((p) => {
        const first = p.name.split(/\s+/)[0] ?? p.name;
        return (
          <SheetPages
            key={p.key}
            doc={{
              size: 'half',
              title:
                who === 'returning' ? `Will you chair ${p.area} again?` : 'Would you run an area?',
              sub: `To ${p.name} · Apple Fest ${next}`,
              name:
                who === 'returning'
                  ? 'Chair invitation · returning'
                  : 'Prospect letter · from the chair prospects',
              lead: (
                <>
                  <p className="pr-sub">
                    {who === 'returning'
                      ? `${first} — will you run ${p.area} again in ${next}? Say yes and your lines are yours at the budget meeting${meeting ? ` on ${fullDate(meeting)}` : ''}.`
                      : `${first} — you offered to help lead${p.area ? ` ${p.area}` : ''}. We need chairs in ${next}. It is one weekend, a one-page plan, a phone app, and a chair beside you who has done it. Would you?`}
                  </p>
                  <p className="pr-sub">{signer}</p>
                </>
              ),
              lines: [],
              sign:
                who === 'returning'
                  ? [`Yes · I’ll chair ${p.area}`, 'Not this year']
                  : ['I’m interested', 'Not this year'],
            }}
          />
        );
      })}
    </>
  );
}
